import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { supabase, PHOTOS_BUCKET, storagePathFromPublicUrl } from "@/lib/supabase";

// Contenu éditable (articles ajoutés, images personnalisées, classeurs avant/après)
// stocké dans un fichier JSON du bucket "photos". Aucune table nécessaire.
const PATH = "config/content.json";

const EMPTY = {
  images: {},
  custom: { textile: [], mecanique: [], surfaces: [], auto_supplements: [] },
  albums: [],
};

function normalize(c) {
  c = c || {};
  return {
    images: c.images || {},
    custom: {
      textile: c.custom?.textile || [],
      mecanique: c.custom?.mecanique || [],
      surfaces: c.custom?.surfaces || [],
      auto_supplements: c.custom?.auto_supplements || [],
    },
    albums: c.albums || [],
  };
}

const ContentContext = createContext(null);

export function ContentProvider({ children }) {
  const [content, setContent] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const { data } = supabase.storage.from(PHOTOS_BUCKET).getPublicUrl(PATH);
      const res = await fetch(`${data.publicUrl}?t=${Date.now()}`);
      if (res.ok) setContent(normalize(await res.json()));
      else setContent(EMPTY);
    } catch {
      setContent(EMPTY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const value = useMemo(() => ({ content, loading, reload }), [content, loading, reload]);
  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export const useContent = () => {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error("useContent must be used within ContentProvider");
  return ctx;
};

// Sauvegarde du contenu (admin connecté)
export async function saveContent(content) {
  const blob = new Blob([JSON.stringify(normalize(content))], { type: "application/json" });
  const { error } = await supabase.storage.from(PHOTOS_BUCKET).upload(PATH, blob, {
    upsert: true,
    contentType: "application/json",
    cacheControl: "60",
  });
  return error;
}

// Upload d'une image -> renvoie l'URL publique
export async function uploadImage(file, prefix = "items") {
  const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
  const path = `${prefix}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage.from(PHOTOS_BUCKET).upload(path, file, { upsert: false });
  if (error) throw error;
  return supabase.storage.from(PHOTOS_BUCKET).getPublicUrl(path).data.publicUrl;
}

// Suppression best-effort d'images du stockage à partir de leurs URLs
export function deleteImages(urls) {
  const paths = (urls || []).map(storagePathFromPublicUrl).filter(Boolean);
  if (paths.length) supabase.storage.from(PHOTOS_BUCKET).remove(paths);
}
